import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { ValueFormatterParams } from 'ag-grid-community';
import { isFunction, merge, get, omit, isNil } from 'lodash';

import {
  NegativeNumberType,
  ValueFormatterFn,
  IRoundingFormatOptions,
  RoundingFormatFn,
  PartialRoundingFormatOptions,
  INumberFormatOptions,
} from '@app/shared/types';
import {
  parseToFloat,
  getNumberFormatOptionsWithDefault,
  currencyRoundingFormat,
  floatNumberRoundingFormat,
} from '@app/shared/utils';
import { DEFAULT_DISPLAY_DECIMAL } from '@config/global-constants';
import { NEGATIVE_NUMBER_NEGATIVES_MAPPING } from '@app/shared/constants/negative-number-negatives.mapping';

import { CountryFormatService } from '../country-format';
import { DecimalNumber } from '@app/shared/common';
import { StateService } from '../state-service';
import { lazyInject } from '@app/containers/base/decorators';

/*
  NumberFormatService
  should includes all methods to format and display number-specific values
*/
@Injectable()
export class NumberFormatService {
  @lazyInject(StateService) public stateService: StateService;

  /**
   * currency options
   */
  public formatOpts: IRoundingFormatOptions;

  public constructor(
    public countryFormatService: CountryFormatService,
    public injector: Injector,
  ) {
    this.formatOpts = getNumberFormatOptionsWithDefault({
      precision: this.countryFormatService.getCurrencyPrecision(),
      symbol: this.countryFormatService.getCurrencySymbol(),
      currency: this.countryFormatService.getCurrencyCode(),
      negatives: this.countryFormatService.getNegatives(),
      percentagePrecision: this.countryFormatService.getPercentagePrecision(),
      thousandsSeparator: this.countryFormatService.getThousandsSeparator(),
      decimalSeparator: this.countryFormatService.getDecimalSeparator(),
    });
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * number parser with precision
   *
   * @param value target value
   * @param precision how many decimals places you want to format
   * @returns number string after precision round(will add trailingZeros)
   */
  public numberWithPrecisionParser(
    value: DecimalNumber | number,
    precision: number = this.formatOpts.precision,
  ): string {
    return floatNumberRoundingFormat(
      value?.toString(),
      merge({}, this.formatOpts, {
        precision,
        negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING[NegativeNumberType.Common],
      }),
    );
  }

  /**
   * number formatter
   *
   * @param isCurrency is need add currency
   * @param getNegativeNumberType get negative number type(is highest priority)
   * @param formatOpts number format options
   * @returns formatted param value
   */
  public getNumberFormatter(
    isCurrency: boolean,
    getNegativeNumberType: () => NegativeNumberType = null,
    customFormatOpts?: INumberFormatOptions,
  ): ValueFormatterFn {
    const roundingFormatMethod: RoundingFormatFn = isCurrency
      ? currencyRoundingFormat
      : floatNumberRoundingFormat;

    const formatOpts: INumberFormatOptions = merge(
      {},
      this.formatOpts,
      customFormatOpts,
    );

    const customPrecision = get(customFormatOpts, 'precision');
    const hasCustomPrecision =
      !isNil(customPrecision) && !Number.isNaN(Number(customPrecision));

    const specOpts: PartialRoundingFormatOptions = {
      ...omit(formatOpts, 'onlyKeepValidDecimals'),
      defaultFormatValue: get(
        formatOpts,
        'defaultFormatValue',
        DEFAULT_DISPLAY_DECIMAL,
      ),
      symbol: isCurrency ? formatOpts.symbol : '',
      precision: hasCustomPrecision
        ? Number(customPrecision)
        : this.getCurrentPrecision(isCurrency),
    };
    if (isFunction(getNegativeNumberType)) {
      specOpts.negatives =
        NEGATIVE_NUMBER_NEGATIVES_MAPPING[getNegativeNumberType()];
    }

    // return value formatter function
    return (params: ValueFormatterParams): string => {
      let parsedValue = parseFloat(params.value);
      // handle received parsed value as NaN, roundingFormatMethod value receive type are
      // string or number
      if (isNaN(parsedValue)) parsedValue = 0;
      const roundedValue = roundingFormatMethod(
        parsedValue,
        merge({}, this.formatOpts, specOpts),
      );

      return roundedValue;
    };
  }

  /**
   * @description use for get current xvi format precision;
   * @param isCurrency boolean
   * @returns precision of cost or quantity;
   */
  public getCurrentPrecision(isCurrency: boolean): number {
    return isCurrency
      ? this.stateService.getCurrencyPrecision()
      : this.stateService.getNumberPrecision();
  }

  /**
   * get number diff formatter
   *
   * @param isCurrency is need add currency
   * @param diffFieldPath diff target form field path
   * @returns number diff formatter
   */
  public getDifferNumberFormatter(
    isCurrency: boolean,
    diffFieldPath: string,
  ): ValueFormatterFn {
    const formatter = this.getNumberFormatter(isCurrency);

    return (params: ValueFormatterParams): string => {
      const curValueFormatted = formatter(params);
      if (!params.node.isRowPinned()) {
        return curValueFormatted;
      }

      const curValue = parseToFloat(params.value);
      const targetDiffValue = parseToFloat(params?.data?.[diffFieldPath]);
      // current value - target value
      const diffFormatted = formatter({
        value: curValue - targetDiffValue,
      });

      return `${curValueFormatted} (${diffFormatted})`;
    };
  }

  public percentageNumberFormat(
    value: number,
    precision: number = this.formatOpts.percentagePrecision,
  ): string {
    const percentageNumber = new DecimalNumber(value).mul(100);

    const percentageNumberFormatter = this.numberWithPrecisionParser(
      percentageNumber,
      precision,
    );

    // case 1. before format number: 0.0000001, after format number: 0.00
    // case 2. before format number: -0.0000001, after format number: 0.00
    return `${Number(percentageNumber) > 0 ? '+' : ''}${percentageNumberFormatter}%`;
  }
}
